/* eslint-disable no-console */
import {
  CheckOutlined,
  EditOutlined,
  ReloadOutlined,
  RobotOutlined
} from '@ant-design/icons';
import {Button} from 'antd';
import {marked} from 'marked';
import React, {useState} from 'react';

import {askAIPrompt} from '../../../services/modules/AI/askAIAPI';
import {IField} from '../interface/inputType';

import {useAskAIScope} from './askAIContext';

const promptStates = {
  Initial: 'Initial',
  thinking: 'thinking',
  writing: 'writing',
  done: 'done'
};

const toQuillHtml = (text: string) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  return `<p>${escaped.replace(/\n/g, '<br/>')}</p>`;
};

const markdownToQuillHtml = (text: string) => {
  try {
    const parsed = marked.parse(text, {async: false});

    return typeof parsed === 'string' ? parsed : toQuillHtml(text);
  } catch (error) {
    return toQuillHtml(text);
  }
};

interface PromptStepsProps {
  field: IField | any;
  getFieldValue: (name: string | string[]) => any;
  setFieldValue: (name: string | string[], value: any) => void;
  setSubmitDisabled?: (disabled: boolean) => void;
  userId: string;
  companyId: string;
  innerFieldName?: string | Array<string | number> | undefined;
}

const toPlainAnswer = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const text = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, '\'')
    .replace(/&quot;/g, '"')
    .trim();

  return text === '' ? null : text;
};

const leafFieldName = (
  name: string | Array<string | number> | undefined
): string => {
  if (!name) {
    return '';
  }

  if (typeof name === 'string') {
    return name;
  }

  const parts = name.filter((part): part is string => typeof part === 'string');

  return parts[parts.length - 1] ?? '';
};

const PromptSteps: React.FC<PromptStepsProps> = ({
  field,
  getFieldValue,
  setFieldValue,
  setSubmitDisabled,
  companyId,
  innerFieldName = undefined
}) => {
  const [promptState, setPromptState] = useState({
    state: promptStates.Initial
  });
  const [hasGenerated, setHasGenerated] = useState(false);
  const askAIScope = useAskAIScope();

  const fieldName = innerFieldName ?? field.fieldName;

  const typeOutAnswer = (text: string) => {
    setPromptState({state: promptStates.writing});

    let index = 0;
    let generatedText = '';

    const interval = setInterval(() => {
      const nextChar = text.charAt(index);
      index++;
      generatedText += nextChar;

      if (index >= text.length) {
        clearInterval(interval);

        setTimeout(() => {
          setPromptState({state: promptStates.Initial});
        }, 1000);

        setPromptState({state: promptStates.done});
      }

      setFieldValue(fieldName, markdownToQuillHtml(generatedText));
    }, 10); // 10ms for faster typing effect

    setSubmitDisabled?.(false);
  };

  const handleAskAIClick = () => {
    const fieldKey = leafFieldName(fieldName);
    const platformKey =
      askAIScope?.collection && askAIScope.tabId && fieldKey
        ? `${askAIScope.collection}.${askAIScope.tabId}.${fieldKey}`
        : '';

    const currentAnswer = toPlainAnswer(getFieldValue(fieldName));

    setFieldValue(fieldName, '');
    setPromptState({state: promptStates.thinking});

    askAIPrompt({
      companyId,
      year: askAIScope?.year ?? '',
      questionKey: field.askAIkey ?? '',
      currentAnswer,
      platformKeys: platformKey ? [platformKey] : [],
      regenerate: hasGenerated
    })
      .then((text) => {
        if (!text) {
          setPromptState({state: promptStates.Initial});

          return;
        }

        setHasGenerated(true);
        typeOutAnswer(text);
      })
      .catch((error) => {
        console.error('Ask AI Error:', error);
        setPromptState({state: promptStates.Initial});
      });
  };

  const innerComponent = () => {
    switch (promptState.state) {
      case promptStates.Initial:
        return (
          <div
            style={{
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Button
              type="primary"
              shape="round"
              icon={hasGenerated ? <ReloadOutlined /> : <RobotOutlined />}
              className="ask-ai-button"
              style={{
                backgroundColor: '#FFE3D1',
                borderColor: '#FFE3D1',
                color: '#994f1a',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}
              onClick={handleAskAIClick}
            >
              {hasGenerated ? 'Regenerate' : 'Ask AI'}
            </Button>
          </div>
        );
      case promptStates.thinking:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Thinking...
            <EditOutlined
              style={{
                backgroundColor: '#994f1a',
                borderColor: '#994f1a',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '20px',
                padding: '4px 14px',
                marginLeft: '10px'
              }}
            />
          </div>
        );
      case promptStates.writing:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Writing...
            <EditOutlined
              style={{
                backgroundColor: '#994f1a',
                borderColor: '#994f1a',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '20px',
                padding: '4px 14px',
                marginLeft: '10px',
                transform: 'rotate(180deg)'
              }}
            />
          </div>
        );
      case promptStates.done:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Done
            <CheckOutlined
              style={{
                backgroundColor: '#719a5d',
                borderColor: '#719a5d',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '14px',
                padding: '4px',
                marginLeft: '10px'
              }}
            />
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <div
      id={innerFieldName ?? field.fieldName}
      style={{
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        padding: '10px',
        borderRadius: '6px',
        borderTopLeftRadius: '0',
        borderTopRightRadius: '0',
        borderLeft: '1px solid #d9d9d9',
        borderRight: '1px solid #d9d9d9',
        borderBottom: '1px solid #d9d9d9'
      }}
    >
      {innerComponent()}
    </div>
  );
};

export default PromptSteps;
